/**
 * Atendimentos que não viraram fatura — issue #341.
 *
 * Atendimento concluído que não entra em fatura não é cobrado, e até aqui isso só aparecia no
 * log do servidor. O bloco mostra cada um na tela de faturas, com o motivo em linguagem de quem
 * cuida do financeiro e o caminho para destravar, e deixa conferir de novo na hora em vez de
 * esperar o job da madrugada.
 *
 * Só existe para quem gerencia faturas: é a mesma permissão das duas rotas que ele chama.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Alert, AlertTitle, Box, Button, CircularProgress, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';

import { DataTable, type DataTableColumn } from '../../components';
import { RouteActionButton } from '../../components/common';
import { pode } from '../../authorization/pode';
import { useManifesto } from '../../stores/manifestoStore';
import {
  invoiceService,
  type InvoiceCreationFailure,
  type InvoiceFailureReason,
  type ReconcileResult,
} from '../../services/invoiceService';

const PERMISSAO = 'invoices.manage';

const MOTIVO: Record<InvoiceFailureReason, string> = {
  SEM_CLINICA: 'Atendimento sem clínica',
  EXAME_SEM_VINCULO_PRECO: 'Exame não vinculado à clínica',
  EXAME_SEM_PRECO: 'Exame sem preço cadastrado na clínica',
  SEM_FATURA_ABERTA: 'Nenhuma fatura aberta para o mês',
  ERRO_INESPERADO: 'Erro inesperado ao faturar',
};

const VAZIO = {
  title: 'Nenhum atendimento pendente de faturamento',
  description: 'Todo atendimento concluído já entrou em fatura.',
};

/** O agendamento guarda a data como DD/MM/YYYY ou YYYY-MM-DD; a tela mostra sempre a primeira. */
function dataDoAtendimento(data: string | null): string {
  if (!data) return '-';
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(data);
  return iso ? `${iso[3]}/${iso[2]}/${iso[1]}` : data;
}

function dataHora(valor: string): string {
  return new Date(valor).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function tituloDaFaixa(quantos: number): string {
  return quantos === 1
    ? '1 atendimento concluído não virou fatura'
    : `${quantos} atendimentos concluídos não viraram fatura`;
}

function resumoDaConferencia({ reconciled, failed }: ReconcileResult): string {
  const faturados = reconciled === 1 ? '1 atendimento faturado' : `${reconciled} atendimentos faturados`;
  return `Conferência feita: ${faturados}, ${failed} ainda sem fatura.`;
}

const SaidaDaFalha: React.FC<{ falha: InvoiceCreationFailure }> = ({ falha }) => {
  const exames = falha.clinicId === null ? null : `/providers/${falha.clinicId}/exams`;

  switch (falha.reason) {
    case 'SEM_CLINICA':
      return (
        <RouteActionButton
          para={`/appointments/${falha.appointmentId}/edit`}
          exige={pode('appointment.update')}
          size='small'
          variant='outlined'
        >
          Definir clínica do atendimento
        </RouteActionButton>
      );
    case 'EXAME_SEM_VINCULO_PRECO':
    case 'EXAME_SEM_PRECO':
      if (!exames) break;
      return (
        <RouteActionButton
          para={exames}
          exige={pode('provider_exam.read')}
          size='small'
          variant='outlined'
        >
          {falha.reason === 'EXAME_SEM_PRECO' ? 'Cadastrar preço na clínica' : 'Vincular exame à clínica'}
        </RouteActionButton>
      );
    case 'SEM_FATURA_ABERTA':
      return (
        <Typography variant='body2'>
          Reabra, na lista abaixo, a fatura da clínica para essa empresa no mês do atendimento e
          confira de novo.
        </Typography>
      );
    case 'ERRO_INESPERADO':
      break;
  }

  return (
    <Typography variant='body2'>
      Confira de novo. Se voltar a falhar, envie a mensagem ao suporte.
    </Typography>
  );
};

const COLUNAS: DataTableColumn<InvoiceCreationFailure>[] = [
  {
    id: 'lastAttemptAt',
    label: 'Última tentativa',
    render: (falha) => dataHora(falha.lastAttemptAt),
  },
  {
    id: 'appointment',
    label: 'Atendimento',
    primary: true,
    render: (falha) => (
      <>
        <Typography variant='body2'>{dataDoAtendimento(falha.appointmentDate)}</Typography>
        <Typography variant='body2'>{falha.employeeName ?? '-'}</Typography>
        <Typography variant='caption' color='text.secondary' component='div'>
          {falha.companyName ?? '-'}
        </Typography>
      </>
    ),
  },
  {
    id: 'reason',
    label: 'Motivo',
    render: (falha) => (
      <>
        <Typography variant='body2' fontWeight={600}>
          {MOTIVO[falha.reason] ?? falha.reason}
        </Typography>
        <Typography variant='caption' color='text.secondary' component='div'>
          {falha.message}
        </Typography>
      </>
    ),
  },
  {
    id: 'saida',
    label: 'Como destravar',
    render: (falha) => <SaidaDaFalha falha={falha} />,
  },
  {
    id: 'attempts',
    label: 'Tentativas',
    align: 'right',
    render: (falha) => falha.attempts,
  },
];

export const InvoiceCreationFailuresBlock: React.FC = () => {
  const { manifesto } = useManifesto();
  const { enqueueSnackbar } = useSnackbar();
  const podeGerenciar = manifesto?.permissoes.includes(PERMISSAO) ?? false;
  // A conferência varre a base inteira, e o servidor só a aceita operando na Plataforma.
  const naPlataforma = manifesto?.perspectiva === 'plataforma';

  const [falhas, setFalhas] = useState<InvoiceCreationFailure[] | null>(null);
  const [conferindo, setConferindo] = useState(false);
  const [resultado, setResultado] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      setFalhas(await invoiceService.getCreationFailures());
    } catch {
      // Bloco acessório: falhar aqui não pode derrubar a listagem de faturas.
      setFalhas([]);
    }
  }, []);

  useEffect(() => {
    if (podeGerenciar) carregar();
  }, [podeGerenciar, carregar]);

  const conferirAgora = async () => {
    setConferindo(true);
    setResultado(null);
    try {
      setResultado(resumoDaConferencia(await invoiceService.reconcile()));
      await carregar();
    } catch (err: unknown) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Erro ao conferir o faturamento', {
        variant: 'error',
      });
    } finally {
      setConferindo(false);
    }
  };

  if (!podeGerenciar || falhas === null) return null;

  const botaoConferir = (
    <Button
      size='small'
      variant={falhas.length > 0 ? 'contained' : 'text'}
      color={falhas.length > 0 ? 'warning' : 'primary'}
      disabled={!naPlataforma || conferindo}
      startIcon={conferindo ? <CircularProgress size={16} /> : undefined}
      onClick={conferirAgora}
    >
      Conferir agora
    </Button>
  );

  const avisos = (
    <>
      {!naPlataforma && (
        <Typography variant='caption' color='text.secondary' component='div'>
          A conferência só roda na perspectiva Plataforma. Troque de contexto para executá-la.
        </Typography>
      )}
      {resultado && (
        <Typography variant='body2' component='div'>
          {resultado}
        </Typography>
      )}
    </>
  );

  if (falhas.length === 0) {
    return (
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant='body2' color='text.secondary'>
            Nenhum atendimento pendente de faturamento.
          </Typography>
          {botaoConferir}
        </Box>
        {avisos}
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Alert severity='warning' sx={{ mb: 2 }} action={botaoConferir}>
        <AlertTitle>{tituloDaFaixa(falhas.length)}</AlertTitle>
        <Typography variant='body2'>
          Enquanto não forem destravados, esses atendimentos não são cobrados. Resolva o motivo
          de cada um e confira de novo — a conferência também roda sozinha todo dia.
        </Typography>
        {avisos}
      </Alert>

      <DataTable
        columns={COLUNAS}
        rows={falhas}
        getRowId={(falha) => falha.id}
        emptyState={VAZIO}
      />
    </Box>
  );
};

export default InvoiceCreationFailuresBlock;
